import React, { useEffect, useState } from 'react';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import Dialog from '@/components/Dialog/Dialog';
import Button from '@/components/Button/Button';
import {
	createSequenceTemplate,
	createSequence,
	deleteSequence,
	updateSequence
} from '@/services/sequencesService';
import toast from 'react-hot-toast';
import { IdleIcon, PaperIcon, PlayIcon } from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import VerticalDashedLines from '@/components/VerticalDashedLines/VerticalDashedLines';
import { Tooltip } from '@/components/atoms/Tooltip';
import { useSequences, type SequencesType } from '../../hooks/useSequences';
import { useSequencesContext } from '../../SequencesContext';

export default function SequencesList({
	paginatedSequences
}: {
	paginatedSequences: SequencesType[] | undefined;
}) {
	const { sequences } = useSequencesContext();
	const isSequenceEmpty = sequences?.length === 0;

	return (
		<div className="flex h-full flex-col overflow-y-auto overflow-x-hidden rounded-l-lg border-b border-l border-t border-dm-grey bg-dm-black">
			{isSequenceEmpty && (
				<div className="h-38 bg-dm-black3 px-6 py-4">
					<div className="flex h-full flex-col justify-center gap-1 rounded-2xl bg-dm-black3.5 px-6 py-4">
						<span className="block text-lg font-semibold text-dm-gold">
							You have no sequences yet
						</span>
						<span className="block text-sm text-dm-white">
							Start your first sequence now by clicking the
							&#39;New Sequence&#39; button.
						</span>
					</div>
				</div>
			)}

			{paginatedSequences?.map(sequence => (
				<SequencesItem key={sequence.id} sequence={sequence} />
			))}
		</div>
	);
}

function SequencesItem({ sequence }: { sequence: SequencesType }) {
	const [isEditingName, setIsEditingName] = useState(false);
	const [isDeletingSequence, setIsDeletingSequence] = useState(false);
	const [isDeleteLoading, setIsDeletingLoading] = useState(false);

	const {
		selectedSequence,
		setSelectedSequence,
		setActiveTab,
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep,
		setCurrentPage
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const { fetchSequences } = useSequences();

	const isSelected = sequence.id === selectedSequence?.id;

	function handleSelectSequence() {
		setSelectedSequence(sequence);
		setSelectedStep(sequence.steps[0]);
		setActiveTab('SEQUENCE');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'user_clicked_on_a_sequence'
		});
	}

	const handleDeleteSequenceOpen = () => {
		setIsDeletingSequence(!isDeletingSequence);
	};

	const onConfirmDelete = () => {
		if (!sequence.id) return;
		setIsDeletingLoading(true);
		deleteSequence(sequence.id)
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error deleting sequence! Please try again later.'
					);
				}
				toast.success('Sequence deleted successfully!');
				fetchSequences();
				setIsDeletingSequence(false);
				setIsDeletingLoading(false);
				setSelectedSequence(undefined);
				setActiveTab('SEQUENCE');
			})
			.catch(err => {
				toast.error(err.message);
				setIsDeletingLoading(false);
			});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'User deleted a sequence'
		});
	};

	function handleEditButton() {
		setSelectedSequence(sequence);
		setSelectedTemplate({
			name: sequence.name,
			steps: sequence.steps
		});
		setSelectedStep(sequence.steps[0]);
		setSequencePage('STEPS-CREATION');
		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'User pressed edit button directly on sequence'
		});
	}

	function handleAddToTemplate() {
		createSequenceTemplate({
			name: `${sequence.name} template`,
			steps: sequence.steps,
			description: ''
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error adding template! Please try again later.'
					);
				}
				toast.success('Template added successfully!');
			})
			.catch(err => {
				toast.error(err.message);
			});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'User saved sequence as template'
		});
	}

	function handleDuplicate() {
		createSequence({
			name: `Copy of ${sequence.name}`,
			steps: sequence.steps,
			status: 'DRAFT'
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error duplicating sequence! Please try again later.'
					);
				}

				fetchSequences();

				setCurrentPage(1);

				setTimeout(() => {
					toast.success('Sequence duplicated successfully!');
				}, 1000);
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	const dropdownMenuOptions = [
		{ id: '1', label: 'Rename', onClick: () => setIsEditingName(true) },
		{ id: '2', label: 'Edit', onClick: handleEditButton },
		{ id: '3', label: 'Save as a template', onClick: handleAddToTemplate },
		{ id: '4', label: 'Duplicate', onClick: handleDuplicate },
		{ id: '5', label: 'Remove', onClick: handleDeleteSequenceOpen }
	];

	return (
		<>
			<Dialog
				isOpen={isDeletingSequence}
				handleOpen={handleDeleteSequenceOpen}
			>
				<span className="mb-2 text-2xl font-medium text-orange">
					Confirm delete
				</span>
				<span className="text-lg text-gray-200">
					Are you sure you want to remove the selected sequence?
				</span>
				<div className="flex justify-between">
					<div className="w-1/3">
						<Button
							variant="ghost"
							onClick={handleDeleteSequenceOpen}
						>
							Cancel
						</Button>
					</div>
					<div className="w-2/5">
						<Button variant="danger" onClick={onConfirmDelete}>
							{isDeleteLoading ? 'Deleting...' : 'Confirm Delete'}
						</Button>
					</div>
				</div>
			</Dialog>
			<div
				role="button"
				tabIndex={0}
				onKeyDown={e => e.key === 'Enter' && handleSelectSequence()}
				onClick={handleSelectSequence}
				data-is-selected={isSelected}
				className="flex min-h-24 w-full flex-shrink-0 cursor-pointer border-b border-dm-grey text-dm-mid-grey hover:border-dm-gold hover:bg-dm-black3 data-[is-selected=false]:border-dm-grey data-[is-selected=true]:border-dm-gold data-[is-selected=false]:bg-dm-black data-[is-selected=true]:bg-dm-black3"
			>
				<div className="flex w-full lg:w-auto lg:flex-grow-0">
					<NameEdit
						sequence={sequence}
						isEditingName={isEditingName}
						setIsEditingName={setIsEditingName}
					/>
				</div>
				<VerticalDashedLines />
				<div className="flex w-[13.2rem] flex-col gap-1 gap-y-1 px-5 py-3 font-semibold sm:w-56 lg:w-[17rem] lg:flex-row lg:pl-6 lg:pr-4 lg:pt-2">
					<div className="flex w-full flex-col gap-1 text-xs">
						<div className="flex gap-1 lg:flex-col">
							<span>Businesses</span>
							<span>
								{sequence.analytics?.totalBusinessCount || 0}
							</span>
						</div>
						<div className="flex gap-1 lg:flex-col">
							<span>Active</span>
							<span>
								{sequence.analytics?.activeBusinessCount || 0}
							</span>
						</div>
					</div>
					<div className="flex w-full flex-col gap-1 text-xs">
						<div className="flex gap-1 lg:flex-col">
							<span>Open rate</span>
							<span>{sequence.analytics?.openRate || 0}%</span>
						</div>
						<div className="flex gap-1 lg:flex-col">
							<span>Replies</span>
							<span>{sequence.analytics?.replyCount || 0}</span>
						</div>
					</div>
				</div>
				<VerticalDashedLines />
				<div className="mx-auto flex w-14 flex-shrink-0 flex-grow-0 items-center justify-center sm:w-20 lg:w-10 lg:flex-shrink lg:flex-shrink-0 [&>*>*>*>svg]:rotate-90 [&>*>*>*>svg]:lg:rotate-0 [&>*>*]:justify-center">
					<DropdownMenu
						itemClassName="text-white py-3 px-2 hover:bg-dm-black3"
						options={dropdownMenuOptions}
					>
						<HorizontalDotsButton />
					</DropdownMenu>
				</div>
			</div>
		</>
	);
}

function NameEdit({
	sequence,
	isEditingName,
	setIsEditingName
}: {
	sequence: SequencesType;
	isEditingName: boolean;
	setIsEditingName: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	const { sequences, setSequences, setSelectedSequence } =
		useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const [sequenceName, setSequenceName] = useState(sequence.name);
	const inputRef = React.useRef<HTMLInputElement>(null);

	function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
		setSequenceName(e.target.value);
	}

	function handleFormSubmit() {
		setIsEditingName(false);

		if (sequenceName === sequence.name || !sequence.id) return;

		updateSequence({
			campaignId: sequence.id,
			name: sequenceName
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error editing sequence name! Please try again later.'
					);
				}
				setSequences(
					sequences?.map(c => {
						if (c.id === sequence.id) {
							return {
								...c,
								name: sequenceName
							};
						}
						return c;
					})
				);
				setSelectedSequence({
					...sequence,
					name: sequenceName
				});
			})
			.catch(err => {
				toast.error(err.message);
				setSequenceName(sequence.name);
			});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'user_edited_sequence_name'
		});
	}

	useEffect(() => {
		if (isEditingName && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditingName]);

	return (
		<div className="flex h-full w-full min-w-32 flex-col justify-center gap-1 px-4 lg:h-auto lg:w-[12.25rem]">
			{isEditingName ? (
				<form onSubmit={handleFormSubmit}>
					<input
						type="text"
						ref={inputRef}
						value={sequenceName}
						onBlur={handleFormSubmit}
						onChange={handleNameChange}
						className="w-3/4 bg-light-grey text-sm text-gray-700 lg:text-lg"
						placeholder="Enter sequence name here"
					/>
				</form>
			) : (
				<>
					<Tooltip.Provider>
						<Tooltip.Root>
							<Tooltip.Trigger asChild>
								<span className="line-clamp-1 text-sm text-dm-gold lg:text-lg">
									{sequenceName}
								</span>
							</Tooltip.Trigger>

							<Tooltip.Content>{sequenceName}</Tooltip.Content>
						</Tooltip.Root>
					</Tooltip.Provider>

					<div className="flex items-center gap-3">
						{sequence.status === 'ACTIVE' && (
							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<PlayIcon />
									</Tooltip.Trigger>

									<Tooltip.Content>
										This sequence is currently in progress
										and actively engaging businesses.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						)}
						{sequence.status === 'DRAFT' && (
							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<PaperIcon />
									</Tooltip.Trigger>

									<Tooltip.Content>
										This is a draft sequence saved from your
										last edit session. Complete the setup to
										activate it.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						)}
						{sequence.status === 'IDLE' && (
							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>
										<IdleIcon />
									</Tooltip.Trigger>

									<Tooltip.Content>
										This sequence currently has no active
										processes.
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						)}
						<span className="text-sm text-dm-mid-grey">
							{sequence.steps.length} steps
						</span>
					</div>
				</>
			)}
		</div>
	);
}
